import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useNavigate } from 'react-router-dom';

/**
 * Asks before leaving with unpublished changes: on reload/close (the browser's own prompt) and on
 * in-app links. The app uses a plain BrowserRouter, so react-router's blocker is not available; links
 * are caught at the document instead. Links marked `data-external-link` (new-tab links) pass through.
 * Returns the dialog to render.
 */
export default function useUnsavedChangesGuard(when: boolean) {
  const navigate = useNavigate();
  const [pending, setPending] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!anchor || !anchor.href || anchor.target === '_blank' || anchor.dataset.externalLink === 'true') return;
      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      event.preventDefault();
      event.stopPropagation();
      setPending(url.pathname + url.search);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [when]);

  return (
    <Dialog open={pending !== null} onClose={() => setPending(null)}>
      <DialogTitle>Leave without publishing?</DialogTitle>
      <DialogContent>
        <DialogContentText>Your changes have not been published and will be lost.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPending(null)}>Keep editing</Button>
        <Button
          color='error'
          variant='contained'
          onClick={() => {
            const to = pending;
            setPending(null);
            if (to) navigate(to);
          }}
        >
          Leave
        </Button>
      </DialogActions>
    </Dialog>
  );
}
